import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import {
  Button,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from '@/hooks';
import { PageLoader } from '@/components/shared/PageLoader';
import { Close, ViewInAr as CubeIcon, Search } from '@mui/icons-material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  ZoneStatus,
  Maybe,
  History,
  useSphericalsPerZoneQuery,
  SphericalsPerZone,
  ZonesForStructureQuery,
  WorkflowStages,
  User,
} from '@/__generated__/graphql';
import { AssigneeAvatar } from '../AssigneeAvatar';
import {
  PaginationBox,
  TablePaginationContainer,
  paginationLabelStyles,
  paginationSelectStyles,
  paginationNavigationStyles,
  paginationCountStyles,
  paginationArrowButtonStyles,
  tableCellStyles,
  TableContainerWrapper,
  tableRowStyles,
  searchTextFieldStyles,
  tableHeaderText,
} from './styles';
import { abyssColors, DEFAULT_ZONE_STAGE_COLOR, ZONE_STAGES_COLORS } from '@/theme/colors';
import { useAtomValue } from 'jotai';
import * as state from '@/state';
import { VIEW_IN_3D_TOOLTIP_LABEL } from '@/constants';

type Props = {
  zones: ZonesForStructureQuery['zonesForStructure'];
};

type Row = {
  id: string;
  zoneName: string;
  parts: number;
  points: number;
  sphericals: number;
  priority: string;
  assignee: Maybe<User> | undefined;
  status: Maybe<ZoneStatus> | undefined;
  histories: Maybe<History>[];
  idleTime: string;
  totalTime: string;
  stage: WorkflowStages;
  priorCuboids: number;
  addedCuboids: number;
};

export const ZonesComplexityViewer = ({ zones }: Props) => {
  const router = useRouter();
  const { inspection } = router.query;
  const [totalSphericals, setTotalSphericals] = useState<SphericalsPerZone[]>();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const paginationOptions = [10, 20, 50, 100];
  const allStages = useAtomValue(state.workflowStages);

  const { data: sphericalsPerZoneData, loading } = useSphericalsPerZoneQuery({
    variables: {
      structureId: inspection?.toString(),
    },
  });

  useEffect(() => {
    if (sphericalsPerZoneData?.sphericalsPerZone) {
      setTotalSphericals(sphericalsPerZoneData.sphericalsPerZone ?? []);
    }
  }, [sphericalsPerZoneData?.sphericalsPerZone]);

  const rows = useMemo(() => {
    return zones.map((zone) => {
      const stage =
        allStages.find((stage) => stage.id === zone.workflowStatus?.workflowStageId) ||
        ({} as WorkflowStages);

      return {
        id: zone.id,
        zoneName: zone.name,
        parts: zone?.totalParts ?? 0,
        points: zone?.totalPoints ?? 0,
        sphericals: totalSphericals?.find((data) => data.zoneId === zone.id)?.totalSphericals ?? 0,
        priority: 'Low',
        assignee: zone.workflowStatus?.assignedToUser,
        status: zone.status,
        histories: [],
        idleTime: 'N/A',
        totalTime: 'N/A',
        stage: stage && stage.name ? stage : ({ name: 'N/A' } as WorkflowStages),
        priorCuboids: 0,
        addedCuboids: 0,
      };
    });
  }, [allStages, totalSphericals, zones]);

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const lowerSearch = searchTerm.toLowerCase();
    return rows.filter((row) => row.zoneName.toLowerCase().includes(lowerSearch));
  }, [rows, searchTerm]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleZoneClick = useCallback(
    (zoneName: string, zoneId: string, event: React.MouseEvent) => {
      const url = `/inspection/${inspection}?zone=${zoneName}&zoneid=${zoneId}`;
      if (event.ctrlKey || event.metaKey) {
        window.open(url, '_blank');
      } else {
        router.push(url);
      }
    },
    [inspection, router]
  );

  const columns = [
    { field: 'zoneName', headerName: 'Zone name' },
    { field: 'points', headerName: 'Points' },
    {
      field: 'priorCuboids',
      headerName: 'NO OF PRIOR CUBOIDS',
      letterSpacing: '0.5px',
      size: '12px',
      LineHeight: '16px',
    },
    {
      field: 'addedCuboids',
      headerName: 'NO OF ADDED CUBOIDS',
      letterSpacing: '0.5px',
      size: '12px',
      LineHeight: '16px',
    },
    { field: 'assigneeTo', headerName: 'Assigned to' },
    { field: 'totalTime', headerName: 'Total time' },
    { field: 'totalIdleTime', headerName: 'Total idle time' },
    { field: 'stage', headerName: 'Stage' },
    { field: 'view', headerName: 'View' },
  ];

  if (loading) {
    return (
      <Box sx={{ position: 'relative', top: '25rem' }}>
        <PageLoader />
      </Box>
    );
  }

  return (
    <>
      <TextField
        fullWidth
        data-testid="panel-search-input"
        size="small"
        sx={searchTextFieldStyles}
        placeholder={'Search zones'}
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <Search sx={{ fontSize: '2rem', color: '#6E8198', marginRight: '0.8rem' }} />
            ),
            endAdornment: searchTerm && searchTerm.length > 0 && (
              <InputAdornment position="end">
                <Close
                  sx={{ cursor: 'pointer', fontSize: '1.6rem' }}
                  onClick={() => setSearchTerm('')}
                />
              </InputAdornment>
            ),
          },
        }}
      />
      <TableContainerWrapper>
        <TableContainer
          sx={{ maxHeight: 'calc(100vh - 250px)', overflow: 'auto' }}
          component={Paper}
        >
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => {
                  return (
                    <TableCell key={column.field}>
                      <Typography
                        variant="body2"
                        sx={{
                          ...tableHeaderText,
                          fontSize: column.size,
                          letterSpacing: column.letterSpacing,
                          lineHeight: column.LineHeight,
                        }}
                      >
                        {column.headerName}
                      </Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: Row) => (
                  <TableRow key={row.id} hover sx={tableRowStyles}>
                    <TableCell sx={{ ...tableCellStyles, width: '150px' }} scope="row">
                      {row.zoneName}
                    </TableCell>
                    <TableCell sx={{ ...tableCellStyles }}>{row.points}</TableCell>
                    <TableCell sx={{ ...tableCellStyles, width: '150px' }}>
                      {row.priorCuboids}
                    </TableCell>
                    <TableCell sx={{ ...tableCellStyles, width: '150px' }}>
                      {row.addedCuboids}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        width: '180px',
                        padding: '6px 16px',
                        '& .MuiPaper-root': {
                          width: '140px !important',
                          margin: '0',
                        },
                      }}
                    >
                      {
                        <AssigneeAvatar
                          assigneeName={row.assignee?.name ?? undefined}
                          profileImage={row.assignee?.picture ?? undefined}
                          zoneId={row.id}
                          details={row.stage}
                          sx={{
                            width: '140px !important',
                            margin: '0',
                          }}
                        />
                      }
                    </TableCell>
                    <TableCell sx={{ ...tableCellStyles }}>{row.totalTime}</TableCell>
                    <TableCell sx={{ ...tableCellStyles, width: '220px' }}>
                      {row.idleTime}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        color: ZONE_STAGES_COLORS?.[row.stage.name] ?? DEFAULT_ZONE_STAGE_COLOR,
                      }}
                    >
                      {row.stage.name}
                    </TableCell>

                    <TableCell sx={{ ...tableCellStyles, width: '60px' }}>
                      <Tooltip title={VIEW_IN_3D_TOOLTIP_LABEL} placement="top" arrow>
                        <Button
                          sx={{ p: { sm: '4px', xl: '9px' }, height: 'min-content' }}
                          startIcon={
                            <CubeIcon
                              sx={{
                                color: abyssColors.secondary[600],
                                fontSize: '2rem !important',
                              }}
                            />
                          }
                          onClick={(event) => handleZoneClick(row.zoneName, row.id, event)}
                        >
                          3D
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <PaginationBox>
          <TablePaginationContainer>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={paginationLabelStyles}>Rows per page:</Typography>
              <FormControl size="small">
                <Select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  sx={paginationSelectStyles}
                >
                  {paginationOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                  <MenuItem value={-1}>All</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={paginationNavigationStyles}>
              <Typography sx={paginationCountStyles}>
                {`${page * rowsPerPage + 1}-${Math.min(
                  (page + 1) * rowsPerPage,
                  filteredRows.length
                )} of ${filteredRows.length}`}
              </Typography>
              <IconButton
                onClick={(e) => handleChangePage(e, page - 1)}
                disabled={page === 0}
                sx={paginationArrowButtonStyles}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={(e) => handleChangePage(e, page + 1)}
                disabled={page >= Math.ceil(filteredRows.length / rowsPerPage) - 1}
                sx={paginationArrowButtonStyles}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </TablePaginationContainer>
        </PaginationBox>
      </TableContainerWrapper>
    </>
  );
};
